/**
 * The viewer's supplier rating grade bands — the one read path every rating
 * badge shares. Customer review 11 Sep (item 2).
 *
 * ## Source of truth
 *
 * A supplier's Rating is its combined score (the Rating Formula's weighted OTIF
 * + quality) graded on **`SupplierRatingThreshold`** — the bands Admin Setup →
 * Performance → **Grade Thresholds** lists and edits, for the company whose
 * grading applies to the viewer (a customer user's home company; the switched
 * tenant for a platform admin). Each band carries the label (`display_label`),
 * the colour (`effective_color`, resolved from the company's grade palette) and
 * the range (`min_score`–`max_score`). Every badge — the Supplier Performance
 * Rating column, the Matrix, the Rating filter, the Excel export, the supplier
 * card, the supplier detail header and the rating breakdown — paints the same
 * score with the same band.
 *
 * The OTIF Grade Thresholds (Platinum/Gold/… on OTIF Settings) are a different
 * scale: they colour an OTIF **percentage** on the dashboards and never grade a
 * combined rating.
 *
 * Before this, the bands came from the settings endpoint, which is admin-only:
 * a Customer User got a 403 and fell back to a hard-coded Platinum/Gold/Silver
 * scale with different ranges, and the card, detail header and breakdown modal
 * never asked at all. So one supplier wore two grades depending on who looked.
 *
 * Suppliers get `[]`: they trade with several customers, and each supplier-side
 * row carries its own customer's bands.
 *
 * Concurrent callers share one in-flight request (a suppliers list renders a
 * card per supplier), and nothing is cached past it, so an edit on the Grade
 * Thresholds tab shows on the next page visit.
 *
 * @example
 * const { bands, loading } = useRatingBands();
 * const grade = getGradeInfo(supplier.combined_score, bands);
 *
 * @returns `bands` sorted for grading (order, then highest minimum first) and
 *          `loading` while the first answer is outstanding.
 * @link getGradeInfo in pages/supplier-performance/utils
 */
import { performanceSettingsAPI, SupplierRatingThreshold } from '../lib/api';
import { useView } from '../contexts/ViewContext';
import { useFetchData } from './useFetchData';
import { usePortalMode } from './usePortalMode';

const inFlight = new Map<string, Promise<SupplierRatingThreshold[]>>();

/** Bands in grading order: configured order, then highest minimum first. */
export function sortRatingBands(bands: SupplierRatingThreshold[]): SupplierRatingThreshold[] {
  return [...bands].sort(
    (a, b) => a.order - b.order || parseFloat(b.min_score) - parseFloat(a.min_score),
  );
}

function fetchBands(key: string, group?: number): Promise<SupplierRatingThreshold[]> {
  const pending = inFlight.get(key);
  if (pending) return pending;
  const request = performanceSettingsAPI
    .getRatingBands(group ? { group } : undefined)
    .then((res) => sortRatingBands(res.data ?? []))
    .finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
}

export interface RatingBandsResult {
  bands: SupplierRatingThreshold[];
  loading: boolean;
  error: string | null;
}

export interface RatingBandsOptions {
  /**
   * CR-8215: in a group view, read the GROUP's bands rather than the viewer's.
   * The supplier cards grade a group view with the group's settings whoever is
   * looking, so their badges must use the same group's bands. Off by default:
   * every other badge keeps the viewer's bands.
   */
  followGroupView?: boolean;
}

/**
 * The viewer's configured rating bands (see the module comment).
 *
 * @param options - `followGroupView` reads a group view's own bands (supplier cards)
 * @returns `{ bands, loading, error }` — `bands` is `[]` for suppliers and on error.
 */
export function useRatingBands(options: RatingBandsOptions = {}): RatingBandsResult {
  const { isSupplier, isHelix } = usePortalMode();
  const skip = isSupplier && !isHelix;
  // A platform admin's bands follow the tenant they switched to.
  const { viewKey, getViewQueryParams } = useView();
  const group = options.followGroupView ? getViewQueryParams()['group'] : undefined;
  const key = group ? `${viewKey}|group:${group}` : viewKey;
  const { data, loading, error } = useFetchData<SupplierRatingThreshold[]>(
    () => (skip ? Promise.resolve([]) : fetchBands(key, group)),
    { deps: [skip, key] },
  );
  return { bands: data ?? [], loading, error };
}
